import { CaseDraftPayload, CaseDraftSnapShot } from "app/components/ai-chat-assistant/types";
import { useCallback, useRef, useState } from "react";

const getCaseTypeValueFromSnapshot = (snapshot: CaseDraftSnapShot): number => {
  return snapshot?.CaseDraft?.caseType?.v;
};

const extractDraftFromSnapshot = (snapshot: CaseDraftSnapShot, caseTypeRequired: boolean): CaseDraftPayload | null => {
  const caseTypeValue = getCaseTypeValueFromSnapshot(snapshot);
  if (caseTypeRequired && !caseTypeValue) {
    return null;
  }
  const draft = snapshot?.CaseDraft;
  return draft ? draft : null;
};


/**
 * Hook for managing case draft confirmation workflow.
 * Handles draft state snapshots, tracks user confirmation status, and provides methods to consume confirmed or latest drafts.
 */
// @CodeScene(disable:"Complex Method")
export function useAiConfirmation({
  caseTypeRequired = true
}: { caseTypeRequired?: boolean }) {
  const latestPayloadRef = useRef<CaseDraftPayload | null>(null);
  const userConfirmedRef = useRef(false);
  const [canActOnDraft, setCanActOnDraft] = useState(false);

  const resetConfirmation = useCallback(() => {
    latestPayloadRef.current = null;
    userConfirmedRef.current = false;
    setCanActOnDraft(false);
  }, []);

  const handleStateSnapshot = useCallback((event: { type: string, snapshot?: CaseDraftSnapShot }) => {
    const snapshot = event?.snapshot;
    const payload = extractDraftFromSnapshot(snapshot, caseTypeRequired);

    if (!payload) {
      return;
    }

    latestPayloadRef.current = payload;
    userConfirmedRef.current = !!event?.snapshot?.UserConfirmed;
    setCanActOnDraft(true);
  }, [caseTypeRequired]);

  const consumeConfirmedDraft = useCallback(() => {
    if (!userConfirmedRef.current || !latestPayloadRef.current) {
      return null;
    }

    const payload = latestPayloadRef.current;
    latestPayloadRef.current = null;
    userConfirmedRef.current = false;
    return payload;
  }, []);

  const consumeLatestDraft = useCallback(() => {
    if (!latestPayloadRef.current) {
      return null;
    }

    const payload = latestPayloadRef.current;
    latestPayloadRef.current = null;
    userConfirmedRef.current = false;
    return payload;
  }, []);

  const hasConfirmedDraft = useCallback(() => userConfirmedRef.current, []);

  return {
    resetDraftConfirmation: resetConfirmation,
    consumeConfirmedDraft,
    consumeLatestDraft,
    hasConfirmedDraft,
    handleStateSnapshot,
    canActOnDraft
  };
}
